/**
 * TanStack Query hook for item-level defects during inspection execution
 *
 * Fetches defects for a specific checklist item within an inspection.
 * Replaces the refreshTrigger pattern with query invalidation.
 */

import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";
import { DefectSeverity, DefectStatus } from "@/types";
import { defectsKeys } from "./useDefectsQuery";
import { normalizeDefectImagePaths } from "@/lib/defectImages";

export interface ItemDefect {
  id: string;
  title: string;
  description: string | null;
  severity: DefectSeverity;
  status: DefectStatus;
  created_by: string | null;
  assigned_to: string | null;
  due_date: string | null;
  images: string[] | null;
}

// Query key factory
export const itemDefectsKeys = {
  all: ["item-defects"] as const,
  byItem: (inspectionId: string, checklistItemId: string) =>
    [...itemDefectsKeys.all, inspectionId, checklistItemId] as const,
  byInspection: (inspectionId: string) => [...itemDefectsKeys.all, inspectionId] as const,
};

async function fetchItemDefects(
  inspectionId: string,
  checklistItemId: string,
): Promise<ItemDefect[]> {
  const { data, error } = await supabase
    .from("defects")
    .select("id, title, description, severity, status, created_by, assigned_to, due_date, images")
    .eq("inspection_id", inspectionId)
    .eq("checklist_item_id", checklistItemId)
    .is("deleted_at", null)
    .order("created_at", { ascending: false });

  if (error) throw error;

  return (data || []).map((d) => ({
    ...d,
    images: normalizeDefectImagePaths(d.images),
  }));
}

export function useItemDefectsQuery(
  inspectionId: string,
  checklistItemId: string,
  enabled: boolean = true,
) {
  const queryClient = useQueryClient();
  useRealtimeSubscription("defects", () =>
    queryClient.invalidateQueries({ queryKey: itemDefectsKeys.all }),
  );

  return useQuery({
    queryKey: itemDefectsKeys.byItem(inspectionId, checklistItemId),
    queryFn: () => fetchItemDefects(inspectionId, checklistItemId),
    enabled: enabled && !!inspectionId && !!checklistItemId,
    staleTime: 30000,
  });
}

export function useSoftDeleteDefectMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (defectId: string) => {
      const { error } = await supabase
        .from("defects")
        .update({ deleted_at: new Date().toISOString(), updated_at: new Date().toISOString() })
        .eq("id", defectId);

      if (error) throw error;
      return defectId;
    },
    onSuccess: () => {
      // Invalidate all item defects and main defects list
      queryClient.invalidateQueries({ queryKey: itemDefectsKeys.all });
      queryClient.invalidateQueries({ queryKey: defectsKeys.all });
      // Note: Badge counts are not invalidated here as this hook doesn't have access to tenantId/userId.
      // The caller should handle badge invalidation if needed, or rely on realtime updates.
    },
  });
}
